import React from "react";
import {
  ContextualMenuItemType,
  ICommandBarItemProps,
  IContextualMenuItem,
  IContextualMenuProps,
} from "@fluentui/react";
import { logger, Separator } from "cayo.ui";
import {
  AlertRule,
  Entity,
  Job,
  OrderByDirection,
  SavedQuery,
} from "@cayo/shared/api/types/cayo-graph";
import { toSavedQuery } from "@cayo/shared/components/GridContainer/gridModel/savedQuery.portal";
import {
  DispatchAction,
  GridControllerAction,
  LoadingAction,
  IGridState,
} from "@cayo/shared/components/GridContainer/types";
import { UIInteraction } from "@cayo/shared/hooks/useUIInteraction";
import { IActionBuilder } from "@cayo/shared/scheme/actions";
import { IPropertiesPanelSignals } from "@cayo/shared/services/propertiesPanelSignal.service";
import { appSettings } from "@cayo/shared/settings/appSettings";
import GridViewSettings from "@cayo/shared/settings/types/GridViewSettings";
import { objectUtils } from "@cayo/shared/utils/object";
import { CommandBarProps } from ".";
import commandBarButtonIds from "./buttons-ids";
import { CommandBarIcon } from "./ui-components";
import { IBriefGridInfo, ICommandBarItem } from "../../api/schema.api";
import MaxResultsDropDown from "../MaxResultsDropDown";

// TODO: Remove this once the action name is included in the async operation response (Graph API).
let lastCommand: any;
export const getLatestCommandName = () => {
  try {
    return lastCommand;
  } finally {
    lastCommand = undefined;
  }
};

const maxResultKey = commandBarButtonIds.maxResults;

const log = logger.getLogger("CommandBarController");

class CommandBarController {
  public readonly items?: ICommandBarItemProps[];
  private selectedItems?: Entity[];
  private briefGridInfo?: IBriefGridInfo;
  private gridViewSettings?: GridViewSettings | undefined;
  private isInPropsPanel?: boolean;

  dispatch: DispatchAction;
  gridState?: IGridState;
  uiInteraction: UIInteraction;
  savedQuery?: SavedQuery | { alertRule?: AlertRule; job?: Job };

  signals?: IPropertiesPanelSignals;

  constructor(
    props: CommandBarProps,
    readonly actionBuilder: IActionBuilder
  ) {
    this.isInPropsPanel = !!props?.object;
    this.signals = props.signals;

    // TODO: do not call model.dispatch in processResponse in case toolbar in the properties panel
    this.dispatch =
      props.dispatch ??
      (props?.signals?.send
        ? (a: GridControllerAction) => {
            const signalPrms = (a as LoadingAction)?.loading;
            props?.signals?.send(a.kind as any, signalPrms);
            return this.dispatchStub();
          }
        : this.dispatchStub);

    this.gridState = props.gridState;
    this.uiInteraction = props.uiInteraction;
    this.items = this.mapToCommandBarItems(props.scheme?.items);

    if (props.maxResults) {
      const c: ICommandBarItemProps = {
        key: maxResultKey,
        itemType: ContextualMenuItemType.Normal,
        commandBarButtonAs: () => {
          return (
            <MaxResultsDropDown
              maxResults={props.maxResults!}
              query={props.gridState?.query}
              dispatch={props.dispatch!}
              previewQueryId={props.previewQueryId}
            />
          );
        },
      };

      this.items = [c, ...(this.items || [])];
    }
  }

  public update = (gridState?: IGridState) => {
    this.gridState = gridState;

    const hasSelection = !!gridState?.selectedItems?.length;
    const multipleSelected = hasSelection && gridState?.selectedItems?.length > 1;

    this.selectedItems = gridState?.selectedItems;
    this.briefGridInfo = this.getGridBriefInfo(gridState);
    this.gridViewSettings = gridState?.gridSettingsKey
      ? appSettings.viewSetting.getSubKey(gridState?.gridSettingsKey)
      : undefined;

    this.savedQuery = gridState?.query ? toSavedQuery(gridState.query) : undefined;
    if (this.savedQuery && gridState?.query?.job) {
      this.savedQuery.job = gridState?.query?.job;
    }

    if (this.savedQuery && gridState?.query?.alertRule) {
      this.savedQuery.alertRule = gridState?.query?.alertRule;
    }

    this.items?.forEach(
      (i) =>
        i.key !== commandBarButtonIds.maxResults &&
        this.updateItem(i, hasSelection, multipleSelected)
    );
    this.hideDisabledDuplicates(multipleSelected);
  };

  private hideDisabledDuplicates = (multipleSelected: boolean) => {
    if (this.items?.length) {
      const groups = objectUtils.groupBy(this.items, (i) => i.text);
      for (const group of groups) {
        if (group[1].length < 2) {
          continue;
        }

        const duplicates = group[1] as ICommandBarItemProps[];
        duplicates
          .filter((d) => d.multiselect !== multipleSelected)
          .forEach((i) => {
            i.visible = false;
          });
        log.debug(group);
      }
    }
  };

  private isJobDependentBinding = (bindings?: { [key: string]: string }): boolean => {
    if (!bindings) {
      return false;
    }
    return Object.values(bindings).some((v) => v.includes("savedQuery.job"));
  };

  private updateItem = (
    item: ICommandBarItemProps,
    hasSelection: boolean,
    multiselect: boolean
  ) => {
    if (item.itemType === ContextualMenuItemType.Divider) {
      return;
    }

    const data = item.data as ICommandBarItem;
    const logPrefix = `updateItem "${item.data.key}"`;
    if (data.key === "removeLink") {
      log.debug("dd");
    }

    if (!this.gridState?.query?.jobResolved && this.isJobDependentBinding(data.bindings)) {
      item.visible = false;
      return;
    }

    const selectedItem = this.selectedItems?.length ? (this.selectedItems[0] as any) : {};
    log.debug(`${logPrefix} ->  args`, { item, hasSelection, multiselect, selectedItem });

    if (data.multiselect !== undefined) {
      item.visible = true;
    }

    let toResolveBindings: boolean = false;
    if (this.gridViewSettings) {
      let addProps: any;
      if (item.data.bindings) {
        addProps = {
          selectedItemsCount: this.selectedItems?.length,
          selectedObjects: this.selectedItems,
        };
      }

      toResolveBindings = (hasSelection && data.dependsOnSelection) || !data.dependsOnSelection;
      log.debug(`${logPrefix} -> toResolveBindings`, toResolveBindings);
      if (toResolveBindings) {
        this.actionBuilder.resolveBindings(item, data.bindings, {
          ...selectedItem,
          view: this.gridViewSettings as any,
          savedQuery: this.savedQuery,
          ...addProps,
        });
      }

      item.subMenuProps?.items?.forEach((i) => {
        if (toResolveBindings) {
          this.actionBuilder.resolveBindings(i, i.data.bindings, {
            view: this.gridViewSettings as any,
            ...addProps,
          });
        }

        this.updateSubMenuItem(i, hasSelection, multiselect);
      });
    }

    if (data.dependsOnSelection) {
      const disableBindingApplyed =
        toResolveBindings && !!data.bindings && Object.keys(data.bindings).includes("disabled");
      log.debug(`${logPrefix} -> disableBindingApplyed`, disableBindingApplyed);
      if (!disableBindingApplyed) {
        item.disabled = !hasSelection || (!data.multiselect && multiselect);
      }

      this.handleCategories(item);
    } else {
      delete item.disabled;
    }

    if (this.isInPropsPanel) {
      this.actionBuilder.resolveBindings(item, data.bindings, { ...selectedItem });
    }
  };

  private updateSubMenuItem = (
    submenuItem: ICommandBarItemProps,
    hasSelection: boolean,
    multiselect: boolean
  ) => {
    const submenuData = submenuItem.data as ICommandBarItem;

    if (submenuData.dependsOnSelection) {
      const hasDisabledBinding =
        submenuData.bindings && !!Object.keys(submenuData.bindings).find((b) => b === "disabled");

      if (!hasDisabledBinding) {
        submenuItem.disabled = !hasSelection || (!submenuData.multiselect && multiselect);
      }
    }
  };

  private handleCategories = (item: ICommandBarItemProps) => {
    const data = item.data as ICommandBarItem;

    if (!item.disabled && data.disabledCategories) {
      const restorableItem = this.selectedItems?.find((i) => {
        const categories = (i as any).categories;
        if (!Array.isArray(categories)) {
          return false;
        }

        return (categories as ICategoryItem[])?.find((c) =>
          data.disabledCategories?.find((ds) => {
            const catName = c?.name?.toLowerCase();
            const disCatName = ds?.toLowerCase();
            return catName === disCatName && c?.value === "Disabled";
          })
        );
      });

      if (restorableItem) {
        item.disabled = true;
      }
    }
  };

  private mapToCommandBarItems = (items?: ICommandBarItem[]) => {
    const result: ICommandBarItemProps[] = [];

    items?.forEach((item, i) => {
      const convertedItem = this.commandBarItemConverter(item, i);
      result.push(convertedItem);

      // Check if the current item has the separator property set to true
      if (item.separator && i !== items.length - 1) {
        // Insert an item with type 'divider' right after the current item
        result.push({
          key: `sep_${item.key}`,
          itemType: ContextualMenuItemType.Divider,
          commandBarButtonAs: () => <Separator vertical />,
        } as ICommandBarItemProps);
      }
    });

    return result;
  };

  private commandBarItemConverter = (
    item: ICommandBarItem,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    index: number
  ): ICommandBarItemProps => {
    const iconName = item?.icon?.iconName;
    const checked = item.checked;

    const result: ICommandBarItemProps = {
      key: item.key,
      text: item.text,
      canCheck: item.canCheck,
      checked: checked,
      disabled: item.disabled,
      data: item,
      iconProps: item.icon?.iconName ? { iconName: item.icon?.iconName } : undefined,
      split: item.split,
      multiselect: item.multiselect,
      ariaLabel: item.text || iconName,
      title: item.tooltip,
      splitButtonAriaLabel: "See options",
      // used to display in primary menu (visible command bar items, not in overflow menu)
      buttonStyles: item.isDefault ? { label: { fontWeight: 500 } } : undefined,
      // used to display in overflow menu
      itemProps: item.isDefault ? { styles: { label: { fontWeight: 500 } } } : undefined,
      subMenuProps: item.subMenuProps?.length
        ? ({
            items: item.subMenuProps && this.mapToCommandBarItems(item.subMenuProps),
          } as IContextualMenuProps)
        : undefined,

      onRenderIcon: (ic: any) =>
        iconName ? (
          <CommandBarIcon ic={ic} overlayIcon={item?.overlayIcon} iconName={iconName} />
        ) : null,

      onClick: (e, item) => {
        this.onItemClick(e, item);
      },
    };

    return result;
  };

  onItemClick = async (
    ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    item?: IContextualMenuItem
  ) => {
    const cmdBarItem = item?.data as ICommandBarItem;
    const action = cmdBarItem.action;
    const onClick = cmdBarItem.onClick;
    const ajaxAction = cmdBarItem.ajaxAction;

    if (onClick) {
      return onClick(ev, item);
    }

    if (action) {
      lastCommand = item?.text;
      await Promise.all(
        this.actionBuilder.buildAction(action!, {
          parentId: this.gridState?.id,
          objects: cmdBarItem.multiselect !== undefined ? this.selectedItems : undefined,
          confirmationText: ajaxAction?.confirmation?.description,
          gridState: this.gridState,
          actionParameters: cmdBarItem.actionParameters,
          dispatch: this.dispatch,
          onSaveComplete: () => {
            if (cmdBarItem.multiselect !== undefined) {
              if (this.isInPropsPanel && action.endsWith("delete")) {
                // TODO: make via UI scheme
                this.signals?.send("close");
                return;
              }

              return this.dispatch({ kind: "refresh" });
            }

            return undefined;
          },
        })
      );
    }

    if (ajaxAction) {
      this.actionBuilder.buildAjaxAction(
        ajaxAction,
        cmdBarItem.multiselect || cmdBarItem.dependsOnSelection ? this.selectedItems : undefined,
        {
          gridInfo: this.briefGridInfo,
          gridId: this?.gridState?.id as any,
          view: this.gridViewSettings,
          gridState: this.gridState,
          savedQuery: this.savedQuery,
          dispatch: this.dispatch,
        },
        () => this.dispatch({ kind: "loading", loading: true }),
        (error) => {
          if (!error) {
            (this.dispatch({ kind: "reloadQuery" }) as Promise<void>).finally(() => {
              if (cmdBarItem.multiselect !== undefined || cmdBarItem.key?.indexOf(".create") > 0) {
                this.dispatch({ kind: "refresh" });
              } else {
                this.dispatch({ kind: "loading", loading: false });
              }
            });
          } else {
            this.dispatch({ kind: "loading", loading: false });
          }

          return Promise.resolve();
        }
      );
    }
  };

  private getGridBriefInfo = (gridState?: IGridState) => {
    try {
      const columns = gridState?.columns?.filter((c) => !!c);
      const sortedColumn = columns?.find((c) => c?.isSorted);
      const defaultOrderBy = sortedColumn?.fieldName;
      const defaultOrderDirection: OrderByDirection = sortedColumn?.isSortedDescending
        ? "descending"
        : "ascending";

      const result: IBriefGridInfo = {
        filter: gridState?.query?.effectiveFilter,
        orderBy: gridState?.query?.orderBy?.field ?? defaultOrderBy,
        orderDirection: gridState?.query?.orderBy?.direction ?? defaultOrderDirection,
        properties: !columns ? undefined : columns.map((c) => c.fieldName!),
      };

      return result;
    } catch (e) {
      log.error("Invalid grid state", gridState);
      throw e;
    }
  };

  private dispatchStub = () => {
    return Promise.resolve();
  };
}

interface ICategoryItem {
  name: string;
  value: string;
}

export default CommandBarController;
